import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { writeupAPI, statsAPI } from "@/lib/api";
import { writeupPath } from "@/lib/slug";
import WriteupCard from "@/components/WriteupCard";
import SEO from "@/components/SEO";
import { Search, Terminal, X } from "lucide-react";

/* ─────────────────────────────────────────
   FONT + STYLE INJECTION  (CRA-safe)
───────────────────────────────────────── */
function useWPStyles() {
  useEffect(() => {
    if (!document.querySelector("link[data-ctf-fonts]")) {
      const l = document.createElement("link");
      l.rel  = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Syne:wght@700;800&family=JetBrains+Mono:wght@300;400;500;700&display=swap";
      l.setAttribute("data-ctf-fonts", "1");
      document.head.appendChild(l);
    }
    if (!document.querySelector("style[data-wp]")) {
      const s = document.createElement("style");
      s.setAttribute("data-wp", "1");
      s.textContent = WP_CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const DIFF_CFG = {
  easy:   { color: "#00ff88", rgb: "0,255,136"  },
  medium: { color: "#ff8c42", rgb: "255,140,66" },
  hard:   { color: "#ff4455", rgb: "255,68,85"  },
  insane: { color: "#cc44ff", rgb: "204,68,255" },
};

const PLATFORM_LABELS = {
  htb: "Hack The Box",
  proving_grounds: "Proving Grounds",
  other: "Other",
};

const formatDifficulty = (difficulty) =>
  difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : "Medium";

/* ─────────────────────────────────────────
   MAIN
───────────────────────────────────────── */
const WriteupsPage = () => {
  useWPStyles();

  const [searchParams, setSearchParams] = useSearchParams();
  const [writeups,  setWriteups]  = useState([]);
  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState(searchParams.get("search") || "");
  const [difficulty,setDifficulty]= useState(searchParams.get("difficulty") || "");
  const [platform,  setPlatform]  = useState(searchParams.get("platform") || "");
  const [skill,     setSkill]     = useState(searchParams.get("skill") || "");
  const [osType,    setOsType]    = useState(searchParams.get("os_type") || "");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      ["search","difficulty","platform","skill","tag","os_type"].forEach(k => {
        const v = searchParams.get(k);
        if (v) params[k] = v;
      });
      const [wr, st] = await Promise.all([writeupAPI.getAll(params), statsAPI.get()]);
      setWriteups(wr.data);
      setStats(st.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => { e.preventDefault(); updateParams({ search }); };

  const updateParams = (next) => {
    const p = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => v ? p.set(k, v) : p.delete(k));
    setSearchParams(p);
  };

  const clearFilters = () => {
    setSearch(""); setDifficulty(""); setPlatform(""); setSkill(""); setOsType("");
    setSearchParams(new URLSearchParams());
  };

  const hasFilters = search || difficulty || platform || skill || osType || searchParams.get("tag");
  const activePlatform = searchParams.get("platform") || platform || "";
  const featuredWriteup = !loading && writeups.length > 0 ? writeups[0] : null;
  const archiveWriteups = !hasFilters && featuredWriteup
    ? writeups.filter((writeup) => writeup.id !== featuredWriteup.id)
    : writeups;

  return (
    <div className="wp-root" data-testid="writeups-page">
      <SEO
        title="Writeups"
        description="CTF writeups and penetration testing walkthroughs."
      />

      {/* ── PAGE HEADER ── */}
      <div className="wp-header">
        <div className="wp-header-grid"  aria-hidden="true" />
        <div className="wp-header-glow"  aria-hidden="true" />
        <div className="wp-header-inner">
          <div className="wp-breadcrumb">
            <span className="wp-prompt">$</span>
            find /writeups -type f | grep -i exploit
          </div>
          <h1 className="wp-title">Writeup Archive</h1>
          <p className="wp-subtitle">Detailed walkthroughs of CTF machines and challenges</p>

          {/* stats row */}
          {stats && (
            <div className="wp-stats-row">
              <div className="wp-stat">
                <span className="wp-stat-num">{stats.machines_owned || 0}</span>
                <span className="wp-stat-label">Machines Owned</span>
              </div>
              {stats.difficulty_distribution && (
                <>
                  <div className="wp-stat-div" />
                  {Object.entries(DIFF_CFG).map(([d, cfg]) => (
                    stats.difficulty_distribution[d] > 0 && (
                      <div className="wp-stat" key={d}>
                        <span className="wp-stat-num" style={{ color: cfg.color }}>
                          {stats.difficulty_distribution[d]}
                        </span>
                        <span className="wp-stat-label" style={{ textTransform: "capitalize" }}>{d}</span>
                      </div>
                    )
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="wp-body">

        {/* search bar */}
        <div className="wp-search-wrap">
          <form onSubmit={handleSearch} className="wp-search-form">
            <div className="wp-search-inner">
              <Search className="wp-search-icon" size={16} />
              <input
                type="text"
                placeholder="Search by title, machine, CVE..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="wp-search-input"
                data-testid="writeups-search-input"
              />
            </div>
            <button type="submit" className="wp-search-btn" data-testid="writeups-search-btn">
              <Search size={14} /> Search
            </button>
          </form>
        </div>

        {/* platform quick tabs */}
        <div className="wp-tabs">
          {[
            { key: "", label: "All" },
            { key: "htb",             label: "Hack The Box" },
            { key: "proving_grounds", label: "Proving Grounds" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setPlatform(t.key); updateParams({ platform: t.key }); }}
              className={`wp-tab ${activePlatform === t.key ? "is-active" : ""}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="wp-layout">

          {/* ── SIDEBAR ── */}
          <aside className="wp-sidebar">

            {/* difficulty */}
            <div className="wp-panel">
              <div className="wp-panel-title">
                <span className="wp-panel-prefix">// </span>By Difficulty
              </div>
              <div className="wp-filter-list">
                {["easy","medium","hard","insane"].map(d => {
                  const cfg = DIFF_CFG[d];
                  const active = difficulty === d;
                  const count = stats?.difficulty_distribution?.[d] || 0;
                  // A filter that returns nothing is not a filter — it is a
                  // public note about what the archive is missing. Hide it
                  // until there is at least one machine at that difficulty.
                  if (!count && !active) return null;
                  return (
                    <button
                      key={d}
                      onClick={() => { const v = active ? "" : d; setDifficulty(v); updateParams({ difficulty: v }); }}
                      className="wp-filter-btn"
                      style={active ? { borderColor: cfg.color, color: cfg.color, background: `rgba(${cfg.rgb},.07)` } : {}}
                      data-testid={`filter-difficulty-${d}`}
                    >
                      <span style={{ textTransform: "capitalize" }}>{d}</span>
                      <span className="wp-filter-count">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* platform */}
            <div className="wp-panel">
              <div className="wp-panel-title">
                <span className="wp-panel-prefix">// </span>By Platform
              </div>
              <div className="wp-filter-list">
                {[
                  { key: "htb",             label: "Hack The Box"    },
                  { key: "proving_grounds", label: "Proving Grounds" },
                  { key: "other",           label: "Other"           },
                ].map(p => {
                  const active = platform === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => { const v = active ? "" : p.key; setPlatform(v); updateParams({ platform: v }); }}
                      className={`wp-filter-btn ${active ? "is-green" : ""}`}
                      data-testid={`filter-platform-${p.key}`}
                    >
                      <span>{p.label}</span>
                      <span className="wp-filter-count">
                        {stats?.platform_distribution?.[p.key] || 0}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* OS */}
            <div className="wp-panel">
              <div className="wp-panel-title">
                <span className="wp-panel-prefix">// </span>By OS
              </div>
              <div className="wp-os-row">
                {["linux","windows"].map(os => (
                  <button
                    key={os}
                    onClick={() => { const v = osType === os ? "" : os; setOsType(v); updateParams({ os_type: v }); }}
                    className={`wp-os-btn ${osType === os ? "is-green" : ""}`}
                    data-testid={`filter-os-${os}`}
                  >
                    {os === "linux" ? "🐧" : "🪟"} {os}
                  </button>
                ))}
              </div>
            </div>

            {/* skills */}
            {stats?.popular_skills?.length > 0 && (
              <div className="wp-panel">
                <div className="wp-panel-title">
                  <span className="wp-panel-prefix">// </span>Skills
                </div>
                <div className="wp-tag-cloud">
                  {stats.popular_skills.slice(0, 10).map(s => (
                    <button
                      key={s.skill}
                      onClick={() => { const v = skill === s.skill ? "" : s.skill; setSkill(v); updateParams({ skill: v }); }}
                      className={`wp-tag-btn ${skill === s.skill ? "is-active" : ""}`}
                    >
                      {s.skill}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* tags */}
            {stats?.popular_tags?.length > 0 && (
              <div className="wp-panel">
                <div className="wp-panel-title">
                  <span className="wp-panel-prefix">// </span>Tags
                </div>
                <div className="wp-tag-cloud">
                  {stats.popular_tags.slice(0, 12).map(t => (
                    <Link
                      key={t.tag}
                      to={`/writeups?tag=${t.tag}`}
                      className={`wp-tag-btn ${searchParams.get("tag") === t.tag ? "is-active" : ""}`}
                    >
                      #{t.tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* clear */}
            {hasFilters && (
              <button onClick={clearFilters} className="wp-clear-btn" data-testid="clear-filters">
                <X size={13} /> Clear All Filters
              </button>
            )}

          </aside>

          {/* ── MAIN ── */}
          <main className="wp-main">
            {!hasFilters && featuredWriteup && (
              <section className="wp-featured-shell" aria-label="Featured writeup">
                <div className="wp-featured-kicker">
                  <span className="wp-prompt">root@archive:~$</span>
                  cat featured-writeup.txt
                </div>
                <div className="wp-featured-frame">
                  <div className="wp-featured-copy">
                    <span className="wp-featured-label">Featured Walkthrough</span>
                    <h2 className="wp-featured-title">{featuredWriteup.title}</h2>
                    <p className="wp-featured-desc">
                      {featuredWriteup.description || "A polished breakdown of reconnaissance, exploitation, and privilege escalation with reusable tactics."}
                    </p>
                    <div className="wp-featured-meta">
                      <div className="wp-featured-stat">
                        <span className="wp-featured-stat-num">{featuredWriteup.machine_name || "Target"}</span>
                        <span className="wp-featured-stat-label">machine</span>
                      </div>
                      <div className="wp-featured-stat">
                        <span className="wp-featured-stat-num">{PLATFORM_LABELS[featuredWriteup.platform] || "Other"}</span>
                        <span className="wp-featured-stat-label">platform</span>
                      </div>
                      <div className="wp-featured-stat">
                        <span className="wp-featured-stat-num">{formatDifficulty(featuredWriteup.difficulty)}</span>
                        <span className="wp-featured-stat-label">difficulty</span>
                      </div>
                    </div>
                    <Link to={writeupPath(featuredWriteup)} className="wp-featured-link">
                      Open featured writeup ->
                    </Link>
                  </div>
                </div>
              </section>
            )}

            {/* active filters */}
            {hasFilters && (
              <div className="wp-active-filters">
                {difficulty && (
                  <span className="wp-active-tag">
                    difficulty:{difficulty}
                    <button onClick={() => { setDifficulty(""); updateParams({ difficulty: "" }); }}><X size={11} /></button>
                  </span>
                )}
                {platform && (
                  <span className="wp-active-tag">
                    platform:{platform}
                    <button onClick={() => { setPlatform(""); updateParams({ platform: "" }); }}><X size={11} /></button>
                  </span>
                )}
                {skill && (
                  <span className="wp-active-tag">
                    skill:{skill}
                    <button onClick={() => { setSkill(""); updateParams({ skill: "" }); }}><X size={11} /></button>
                  </span>
                )}
                {searchParams.get("tag") && (
                  <span className="wp-active-tag">
                    tag:#{searchParams.get("tag")}
                    <button onClick={() => updateParams({ tag: "" })}><X size={11} /></button>
                  </span>
                )}
              </div>
            )}

            {/* count */}
            <div className="wp-count">
              <span className="wp-count-prompt">&gt;</span>
              {loading ? "Scanning..." : `${archiveWriteups.length} writeup${archiveWriteups.length !== 1 ? "s" : ""} found`}
            </div>

            {/* grid */}
            {loading ? (
              <div className="wp-grid">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="wp-skeleton">
                    <div className="wp-sk-line wp-sk-short" />
                    <div className="wp-sk-line wp-sk-med"   />
                    <div className="wp-sk-line wp-sk-long"  />
                    <div className="wp-sk-line wp-sk-short" />
                  </div>
                ))}
              </div>
            ) : archiveWriteups.length > 0 ? (
              <div className="wp-grid" data-testid="writeups-list">
                {archiveWriteups.map((w) => (
                  <WriteupCard
                    key={w.id}
                    writeup={w}
                    featured={false}
                  />
                ))}
              </div>
            ) : (
              <div className="wp-empty">
                <Terminal size={40} className="wp-empty-icon" />
                <p className="wp-empty-title">No writeups found</p>
                <p className="wp-empty-sub">
                  {hasFilters ? "Try adjusting your filters" : "Check back soon for new content!"}
                </p>
              </div>
            )}

          </main>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   CSS
───────────────────────────────────────── */
const WP_CSS = `
@keyframes wp-fadein  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
@keyframes wp-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
@keyframes wp-pulse-green { 0%,100%{opacity:.5} 50%{opacity:1} }

.wp-root {
  min-height:100vh;
  background:#070a0e;
  background-image:
    linear-gradient(rgba(0,255,136,.013) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,255,136,.013) 1px,transparent 1px);
  background-size:44px 44px;
  font-family:'JetBrains Mono',ui-monospace,monospace;
}

/* ── HEADER ── */
.wp-header {
  position:relative; overflow:hidden;
  background:#070a0e; padding:4rem 0 2.5rem;
  border-bottom:1px solid #1a2738;
}
.wp-header-grid {
  position:absolute; inset:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(0,255,136,.03) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,255,136,.03) 1px,transparent 1px);
  background-size:44px 44px;
  -webkit-mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.8) 20%,rgba(0,0,0,.8) 80%,transparent);
  mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.8) 20%,rgba(0,0,0,.8) 80%,transparent);
}
.wp-header-glow {
  position:absolute; inset:0; pointer-events:none;
  background:
    radial-gradient(ellipse 700px 300px at 50% 0%,rgba(0,255,136,.07) 0%,transparent 70%),
    radial-gradient(ellipse 400px 250px at 85% 50%,rgba(0,212,255,.04) 0%,transparent 60%);
}
.wp-header-inner {
  position:relative; z-index:1;
  max-width:1200px; margin:0 auto; padding:0 2rem;
  animation:wp-fadein .5s ease both;
}
.wp-breadcrumb {
  display:flex; align-items:center; gap:8px;
  font-size:12px; color:#56657a; margin-bottom:1rem; letter-spacing:.03em;
}
.wp-prompt { color:#00ff88; font-size:14px }
.wp-title  {
  font-family:'Syne',system-ui,sans-serif;
  font-size:clamp(1.8rem,4vw,2.8rem); font-weight:800;
  color:#e2eaf3; letter-spacing:-.02em; margin:0 0 .4rem;
}
.wp-subtitle { font-size:13px; color:#56657a; margin:0 0 1.8rem }

/* stats row */
.wp-stats-row {
  display:flex; align-items:center; gap:2rem; flex-wrap:wrap;
}
.wp-stat { display:flex; flex-direction:column; gap:2px }
.wp-stat-num {
  font-family:'Share Tech Mono',monospace;
  font-size:22px; font-weight:700; color:#00ff88;
  line-height:1;
}
.wp-stat-label { font-size:10px; color:#56657a; letter-spacing:.08em; text-transform:uppercase }
.wp-stat-div   { width:1px; height:32px; background:#1a2738 }

/* ── BODY ── */
.wp-body { max-width:1200px; margin:0 auto; padding:2rem 2rem 6rem }

/* search */
.wp-search-wrap { margin-bottom:1.5rem }
.wp-search-form {
  display:flex; gap:10px;
}
.wp-search-inner {
  position:relative; flex:1;
}
.wp-search-icon {
  position:absolute; left:14px; top:50%; transform:translateY(-50%);
  color:#56657a; pointer-events:none;
}
.wp-search-input {
  width:100%; height:42px;
  padding:0 14px 0 42px;
  background:#0c1118;
  border:1px solid #1a2738;
  border-radius:3px;
  font-family:'JetBrains Mono',monospace; font-size:13px;
  color:#b8c4d0;
  outline:none; transition:border-color .18s;
}
.wp-search-input::placeholder { color:#56657a }
.wp-search-input:focus { border-color:rgba(0,255,136,.4) }
.wp-search-btn {
  display:inline-flex; align-items:center; gap:7px;
  height:42px; padding:0 20px;
  background:rgba(0,255,136,.1);
  border:1px solid rgba(0,255,136,.35);
  border-radius:3px;
  font-family:'JetBrains Mono',monospace; font-size:12px;
  color:#00ff88; cursor:pointer; white-space:nowrap;
  transition:all .18s; letter-spacing:.04em;
}
.wp-search-btn:hover { background:rgba(0,255,136,.18); border-color:rgba(0,255,136,.6) }

/* platform tabs */
.wp-tabs {
  display:flex; gap:8px; flex-wrap:wrap;
  margin-bottom:2rem;
}
.wp-tab {
  font-family:'Share Tech Mono',monospace; font-size:12px;
  padding:5px 16px; border-radius:2px;
  border:1px solid #1a2738; color:#56657a;
  background:transparent; cursor:pointer;
  transition:all .18s; letter-spacing:.06em;
}
.wp-tab:hover  { border-color:#56657a; color:#b8c4d0 }
.wp-tab.is-active {
  border-color:rgba(0,255,136,.45) !important;
  color:#00ff88 !important;
  background:rgba(0,255,136,.07) !important;
}

/* layout */
.wp-layout {
  display:grid;
  grid-template-columns:220px 1fr;
  gap:2.5rem; align-items:start;
}
@media(max-width:860px) {
  .wp-layout { grid-template-columns:1fr }
  .wp-sidebar { display:none }
  .wp-featured-frame { grid-template-columns:1fr }
  .wp-featured-meta { grid-template-columns:repeat(3, minmax(0, 1fr)) }
}

/* ── SIDEBAR ── */
.wp-sidebar { display:flex; flex-direction:column; gap:1rem; position:sticky; top:80px }

.wp-panel {
  background:#0c1118;
  border:1px solid #1a2738;
  border-radius:3px; padding:1rem 1.2rem;
}
.wp-panel-title {
  font-size:10px; text-transform:uppercase; letter-spacing:.12em;
  color:#56657a; margin-bottom:.8rem;
}
.wp-panel-prefix { color:#00ff88 }

.wp-filter-list { display:flex; flex-direction:column; gap:4px }
.wp-filter-btn {
  display:flex; justify-content:space-between; align-items:center;
  width:100%; padding:7px 10px;
  font-family:'JetBrains Mono',monospace; font-size:12px;
  color:#56657a; background:transparent;
  border:1px solid #1a2738; border-radius:2px;
  cursor:pointer; transition:all .18s;
}
.wp-filter-btn:hover { border-color:#56657a; color:#b8c4d0 }
.wp-filter-btn.is-green {
  border-color:rgba(0,255,136,.4) !important;
  color:#00ff88 !important;
  background:rgba(0,255,136,.06) !important;
}
.wp-filter-count {
  font-family:'Share Tech Mono',monospace;
  font-size:11px; color:#2e4a5a;
}

.wp-os-row  { display:flex; gap:6px }
.wp-os-btn  {
  flex:1; padding:7px 0; text-align:center;
  font-family:'JetBrains Mono',monospace; font-size:11px;
  color:#56657a; background:transparent;
  border:1px solid #1a2738; border-radius:2px;
  cursor:pointer; transition:all .18s;
  text-transform:capitalize;
}
.wp-os-btn:hover   { border-color:#56657a; color:#b8c4d0 }
.wp-os-btn.is-green {
  border-color:rgba(0,255,136,.4) !important;
  color:#00ff88 !important; background:rgba(0,255,136,.06) !important;
}

.wp-tag-cloud { display:flex; flex-wrap:wrap; gap:5px }
.wp-tag-btn {
  font-family:'Share Tech Mono',monospace; font-size:11px;
  padding:3px 9px; border-radius:2px;
  border:1px solid #1a2738; color:#56657a;
  background:transparent; cursor:pointer;
  text-decoration:none; transition:all .18s;
}
.wp-tag-btn:hover { border-color:rgba(0,255,136,.35); color:#00ff88 }
.wp-tag-btn.is-active {
  background:#00ff88 !important; color:#000 !important; border-color:#00ff88 !important;
}

.wp-clear-btn {
  display:flex; align-items:center; justify-content:center; gap:6px;
  width:100%; padding:8px;
  font-family:'JetBrains Mono',monospace; font-size:11px;
  color:#56657a; background:transparent;
  border:1px solid #1a2738; border-radius:3px;
  cursor:pointer; transition:all .18s; letter-spacing:.04em;
}
.wp-clear-btn:hover { color:#ff4455; border-color:rgba(255,68,85,.4) }

/* ── MAIN ── */
.wp-main { min-width:0 }

.wp-featured-shell {
  margin-bottom:1.8rem;
  padding:1.3rem;
  border:1px solid rgba(0,255,136,.16);
  border-radius:3px;
  background:
    radial-gradient(circle at top right, rgba(0,255,136,.08), transparent 32%),
    linear-gradient(180deg, rgba(12,17,24,.96) 0%, rgba(7,10,14,.98) 100%);
  box-shadow:0 28px 80px rgba(0,0,0,.28);
}
.wp-featured-kicker {
  display:flex; align-items:center; gap:8px; flex-wrap:wrap;
  margin-bottom:1rem;
  font-size:11px; letter-spacing:.08em; text-transform:uppercase;
  color:#56657a;
}
.wp-featured-frame {
  display:grid;
  grid-template-columns:minmax(0, 1fr);
  gap:1rem; align-items:stretch;
}
.wp-featured-copy {
  display:flex; flex-direction:column; justify-content:center;
  min-width:0;
}
.wp-featured-label {
  display:inline-flex; align-items:center; width:max-content;
  margin-bottom:.95rem; padding:4px 10px;
  border:1px solid rgba(0,212,255,.24); border-radius:999px;
  background:rgba(0,212,255,.08);
  font-family:'Share Tech Mono',monospace; font-size:10px;
  letter-spacing:.18em; text-transform:uppercase; color:#00d4ff;
}
.wp-featured-title {
  margin:0 0 .8rem;
  font-family:'Syne',system-ui,sans-serif;
  font-size:clamp(1.75rem,3vw,2.5rem); font-weight:800;
  line-height:1.08; letter-spacing:-.03em; color:#e2eaf3;
}
.wp-featured-desc {
  max-width:72ch; margin:0 0 1.25rem;
  font-size:14px; line-height:1.8; color:#8ea0b4;
}
.wp-featured-meta {
  display:grid;
  grid-template-columns:repeat(3, minmax(0, 1fr));
  gap:10px;
}
.wp-featured-stat {
  padding:.95rem 1rem;
  border:1px solid #1a2738;
  border-radius:3px;
  background:rgba(7,10,14,.72);
}
.wp-featured-stat-num {
  display:block;
  font-family:'Share Tech Mono',monospace;
  font-size:13px; letter-spacing:.12em; text-transform:uppercase;
  color:#e2eaf3;
}
.wp-featured-stat-label {
  display:block; margin-top:5px;
  font-size:10px; letter-spacing:.16em; text-transform:uppercase;
  color:#56657a;
}
.wp-featured-link {
  display:inline-flex; align-items:center; width:max-content;
  margin-top:1.1rem; padding:10px 14px;
  border:1px solid rgba(0,255,136,.26); border-radius:3px;
  background:rgba(0,255,136,.06);
  font-family:'Share Tech Mono',monospace; font-size:11px;
  letter-spacing:.12em; text-transform:uppercase;
  color:#00ff88; text-decoration:none; transition:all .18s;
}
.wp-featured-link:hover {
  border-color:rgba(0,255,136,.5);
  background:rgba(0,255,136,.12);
}

.wp-active-filters { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:1rem }
.wp-active-tag {
  display:inline-flex; align-items:center; gap:6px;
  font-family:'Share Tech Mono',monospace; font-size:11px;
  padding:4px 10px; border-radius:2px;
  background:#0c1118; border:1px solid #1a2738; color:#b8c4d0;
}
.wp-active-tag button {
  background:none; border:none; padding:0;
  color:#56657a; cursor:pointer; display:flex; align-items:center;
  transition:color .15s;
}
.wp-active-tag button:hover { color:#ff4455 }

.wp-count {
  font-family:'Share Tech Mono',monospace; font-size:12px;
  color:#56657a; margin-bottom:1.2rem;
  display:flex; align-items:center; gap:8px;
}
.wp-count-prompt { color:#00ff88; font-size:14px }

/* grid */
.wp-grid {
  display:grid;
  grid-template-columns:repeat(auto-fill,minmax(300px,1fr));
  gap:1.2rem;
}

/* skeleton */
.wp-skeleton {
  background:#0c1118; border:1px solid #1a2738;
  border-radius:3px; padding:1.4rem;
  display:flex; flex-direction:column; gap:10px;
}
.wp-sk-line {
  height:10px; border-radius:2px;
  background:linear-gradient(90deg,#1a2738 25%,#243448 50%,#1a2738 75%);
  background-size:200% 100%;
  animation:wp-shimmer 1.5s ease infinite;
}
.wp-sk-short { width:35% }
.wp-sk-med   { width:65% }
.wp-sk-long  { width:90% }

/* empty state */
.wp-empty {
  display:flex; flex-direction:column; align-items:center;
  justify-content:center; padding:5rem 2rem;
  border:1px solid #1a2738; border-radius:3px;
  background:#0c1118; text-align:center;
}
.wp-empty-icon  { color:#1a2738; margin-bottom:1rem }
.wp-empty-title { font-family:'Share Tech Mono',monospace; font-size:14px; color:#56657a; margin:0 0 6px }
.wp-empty-sub   { font-size:12px; color:#2e4a5a; margin:0 }

@media(max-width:768px) {
  .wp-search-form { flex-direction:column }
  .wp-search-btn { width:100%; justify-content:center }
  .wp-body { padding:1.4rem 1rem 5rem }
  .wp-header { padding:3.4rem 0 2rem }
  .wp-title { font-size:clamp(1.6rem,8vw,2.2rem) }
  .wp-featured-shell { padding:1rem }
  .wp-featured-meta { grid-template-columns:1fr }
}
`;

export default WriteupsPage;
